import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function IngredientLabels({ 
    labels = [], 
    primaryColor = '#C9A227',
    className = ''
}) {
    const [activeLabel, setActiveLabel] = useState(null);

    if (!labels || labels.length === 0) return null;

    return (
        <div className={`absolute inset-0 pointer-events-none ${className}`} data-testid="ingredient-labels-overlay">
            {labels.map((label, index) => (
                <LabelItem 
                    key={label.id} 
                    label={label} 
                    index={index}
                    primaryColor={primaryColor}
                    isActive={activeLabel === label.id}
                    onTap={() => setActiveLabel(activeLabel === label.id ? null : label.id)}
                />
            ))}
        </div>
    );
}

function LabelItem({ label, index, primaryColor, isActive, onTap }) {
    const benefits = label.subtitle
        ? label.subtitle.split(',').map(s => s.trim()).filter(Boolean)
        : [];

    // Stagger: each label enters 0.6s apart for cinematic sequencing
    const baseDelay = 0.5 + index * 0.6;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: baseDelay, duration: 0.8, ease: 'easeOut' }}
            className="absolute inset-0"
        >
            {/* Connecting Line — draws itself in */}
            <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible', zIndex: 1 }}>
                <motion.line
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.6 }}
                    transition={{ delay: baseDelay + 0.2, duration: 0.7, ease: 'easeInOut' }}
                    x1={`${label.anchor_x_percent}%`}
                    y1={`${label.anchor_y_percent}%`}
                    x2={`${label.card_x_percent}%`}
                    y2={`${label.card_y_percent}%`}
                    stroke="rgba(255,255,255,0.55)"
                    strokeWidth="1"
                    strokeDasharray="4 3"
                />
            </svg>

            {/* Anchor Pin */}
            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: baseDelay + 0.1, type: 'spring', stiffness: 300, damping: 18 }}
                className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
                style={{ left: `${label.anchor_x_percent}%`, top: `${label.anchor_y_percent}%`, zIndex: 2 }}
            >
                {/* Outer glow ring */}
                <motion.div
                    className="absolute rounded-full"
                    style={{
                        inset: '-6px',
                        background: `radial-gradient(circle, ${primaryColor}30 0%, transparent 70%)`,
                    }}
                    animate={isActive ? { scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] } : {}}
                    transition={{ repeat: Infinity, duration: 1.8 }}
                />
                {/* Core pin */}
                <div
                    className="w-3.5 h-3.5 rounded-full"
                    style={{
                        backgroundColor: primaryColor,
                        border: '2px solid rgba(255,255,255,0.85)',
                        boxShadow: `0 0 10px ${primaryColor}70, 0 0 20px ${primaryColor}25`,
                    }}
                />
                {/* Breathing pulse */}
                <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ border: `1px solid ${primaryColor}60` }}
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 2.8, opacity: 0 }}
                    transition={{ repeat: Infinity, duration: 2.5, delay: index * 0.4, ease: 'easeOut' }}
                />
            </motion.div>

            {/* Label Card */}
            <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.85 }}
                animate={{
                    opacity: 1,
                    y: 0,
                    scale: isActive ? 1.08 : 1,
                }}
                transition={{
                    delay: baseDelay + 0.35,
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                    scale: { duration: 0.25 }
                }}
                className="absolute pointer-events-auto cursor-pointer"
                style={{
                    left: `clamp(6px, calc(${label.card_x_percent}% - 55px), calc(100% - 125px))`,
                    top: `${label.card_y_percent}%`,
                    transform: 'translateY(-50%)',
                    zIndex: isActive ? 10 : 3,
                }}
                onClick={onTap}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
            >
                <div
                    className="relative rounded-2xl overflow-hidden transition-all duration-500"
                    style={{
                        backgroundColor: isActive
                            ? 'rgba(30, 38, 18, 0.88)'
                            : 'rgba(38, 46, 24, 0.65)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: isActive
                            ? `1px solid ${primaryColor}60`
                            : '1px solid rgba(255,255,255,0.18)',
                        boxShadow: isActive
                            ? `0 12px 40px rgba(0,0,0,0.5), 0 0 30px ${primaryColor}15, inset 0 1px 0 rgba(255,255,255,0.1)`
                            : '0 6px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
                        minWidth: '100px',
                        maxWidth: '165px',
                    }}
                >
                    {/* Top-edge shine */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: 'linear-gradient(170deg, rgba(255,255,255,0.18) 0%, transparent 35%)',
                            borderRadius: 'inherit',
                        }}
                    />

                    <div className="relative px-3.5 py-3">
                        {/* Title */}
                        <div className="flex items-center gap-2">
                            {label.icon && (
                                <motion.span
                                    className="text-base flex-shrink-0"
                                    animate={isActive ? { scale: [1, 1.2, 1] } : {}}
                                    transition={{ duration: 0.4 }}
                                >
                                    {label.icon}
                                </motion.span>
                            )}
                            <p
                                className="text-[13px] font-semibold tracking-wide leading-tight"
                                style={{
                                    color: 'rgba(255,255,255,0.95)',
                                    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                                    letterSpacing: '0.03em',
                                }}
                            >
                                {label.title}
                            </p>
                        </div>

                        {/* Benefits — expand on active or desktop */}
                        <AnimatePresence>
                            {benefits.length > 0 && (isActive || window.innerWidth > 640) && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.35, ease: 'easeOut' }}
                                    className="mt-1.5 space-y-1"
                                >
                                    {benefits.map((benefit, i) => (
                                        <motion.div
                                            key={benefit}
                                            initial={{ opacity: 0, x: -6 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.08, duration: 0.3 }}
                                            className="flex items-center gap-2"
                                        >
                                            <span
                                                className="w-1 h-1 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: primaryColor, boxShadow: `0 0 4px ${primaryColor}60` }}
                                            />
                                            <span
                                                className="text-[11px] leading-tight"
                                                style={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.02em' }}
                                            >
                                                {benefit}
                                            </span>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
